import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  UploadCloud, Download, Trash2, History, Loader2, FileText, FileSpreadsheet, FileImage, FileArchive, File as FileIcon, FolderOpen,
} from "lucide-react";

const EXT_ICONS = [
  [["pdf", "doc", "docx", "txt", "md", "rtf", "ppt", "pptx"], FileText],
  [["xls", "xlsx", "csv"], FileSpreadsheet],
  [["png", "jpg", "jpeg", "gif", "webp", "svg", "bmp"], FileImage],
  [["zip", "rar", "7z", "tar", "gz"], FileArchive],
];

function iconFor(name) {
  const ext = (name || "").split(".").pop().toLowerCase();
  for (const [exts, Icon] of EXT_ICONS) if (exts.includes(ext)) return Icon;
  return FileIcon;
}

export function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ArtifactsView({ projectId }) {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("Other");
  const [filter, setFilter] = useState("all");
  const [historyOf, setHistoryOf] = useState(null);
  const [versions, setVersions] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const load = async () => {
    try {
      const [a, c] = await Promise.all([
        api.get(`/projects/${projectId}/artifacts`),
        api.get("/artifact-categories"),
      ]);
      setItems(a.data);
      setCategories(c.data);
    } catch (e) {
      toast.error("Could not load artifacts");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 50 * 1024 * 1024) { toast.error(`${file.name} exceeds the 50 MB limit`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", uploadCategory);
      try {
        const { data } = await api.post(`/projects/${projectId}/artifacts`, fd);
        toast.success(data.version > 1 ? `${file.name} updated to v${data.version}` : `${file.name} uploaded`);
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
    load();
  };

  const download = async (a) => {
    try {
      const { data } = await api.get(`/artifacts/${a.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.original_filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.original_filename}" and all its versions?`)) return;
    try {
      await api.delete(`/artifacts/${a.id}`);
      setItems((xs) => xs.filter((x) => x.id !== a.id));
      toast.success("Artifact deleted");
    } catch (e) {
      toast.error("Could not delete");
    }
  };

  const changeCategory = async (a, category) => {
    try {
      await api.patch(`/artifacts/${a.id}`, { category });
      setItems((xs) => xs.map((x) => (x.id === a.id ? { ...x, category } : x)));
    } catch (e) {
      toast.error("Could not update category");
    }
  };

  const openHistory = async (a) => {
    setHistoryOf(a);
    setVersions(null);
    try {
      const { data } = await api.get(`/artifacts/${a.id}/versions`);
      setVersions(data);
    } catch (e) {
      toast.error("Could not load history");
      setHistoryOf(null);
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const usedCategories = [...new Set(items.map((i) => i.category))].sort();
  const visible = filter === "all" ? items : items.filter((i) => i.category === filter);
  const grouped = {};
  for (const it of visible) (grouped[it.category] = grouped[it.category] || []).push(it);

  return (
    <div className="space-y-6" data-testid="artifacts-view">
      {/* Upload zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragOver ? "border-primary bg-primary/5" : "border-border"}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFiles([...e.dataTransfer.files]); }}
        data-testid="artifact-dropzone"
      >
        <UploadCloud className="h-8 w-8 text-primary mx-auto mb-2" />
        <p className="font-semibold text-sm">Drag & drop files here, or</p>
        <div className="flex flex-wrap items-center justify-center gap-2 mt-3">
          <Select value={uploadCategory} onValueChange={setUploadCategory}>
            <SelectTrigger className="h-9 w-44" data-testid="artifact-upload-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="gap-2" disabled={uploading} onClick={() => inputRef.current?.click()} data-testid="artifact-upload-button">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />} {uploading ? "Uploading..." : "Choose files"}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Any file type, up to 50 MB. Re-upload a file with the same name to create a new version.</p>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => uploadFiles([...e.target.files])} data-testid="artifact-file-input" />
      </div>

      {/* Category filter */}
      {items.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="artifact-filters">
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label={`All (${items.length})`} testid="artifact-filter-all" />
          {usedCategories.map((c) => (
            <FilterChip key={c} active={filter === c} onClick={() => setFilter(c)} label={`${c} (${items.filter((i) => i.category === c).length})`} testid={`artifact-filter-${c.toLowerCase().replace(/\s/g, "-")}`} />
          ))}
        </div>
      )}

      {items.length === 0 ? (
        <div className="text-center py-10 text-muted-foreground" data-testid="artifacts-empty">
          <FolderOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
          <p className="text-sm">No artifacts yet. Upload your charter, requirements, or any project document.</p>
        </div>
      ) : (
        Object.entries(grouped).map(([cat, docs]) => (
          <div key={cat} className="border border-border rounded-lg overflow-hidden" data-testid={`artifact-group-${cat.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center gap-2 px-4 py-2.5 bg-secondary/40 border-b border-border">
              <FolderOpen className="h-4 w-4 text-primary" />
              <span className="font-display font-bold text-sm">{cat}</span>
              <span className="text-xs text-muted-foreground">{docs.length}</span>
            </div>
            <div className="divide-y divide-border">
              {docs.map((a) => {
                const Icon = iconFor(a.original_filename);
                return (
                  <div key={a.id} className="flex items-center gap-3 px-4 py-3 hover:bg-secondary/20" data-testid={`artifact-row-${a.id}`}>
                    <Icon className="h-5 w-5 text-primary shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">{a.original_filename}</p>
                        {a.version > 1 && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary shrink-0" data-testid={`artifact-version-${a.id}`}>v{a.version}</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {formatBytes(a.size)} · {a.uploaded_by_name || "Unknown"} · {new Date(a.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Select value={a.category} onValueChange={(v) => changeCategory(a, v)}>
                      <SelectTrigger className="h-8 w-36 text-xs shrink-0 hidden sm:flex" data-testid={`artifact-category-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {a.version > 1 && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => openHistory(a)} title="Version history" data-testid={`artifact-history-${a.id}`}>
                        <History className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => download(a)} title="Download" data-testid={`artifact-download-${a.id}`}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-destructive hover:bg-destructive/10" onClick={() => remove(a)} title="Delete" data-testid={`artifact-delete-${a.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Version history dialog */}
      <Dialog open={!!historyOf} onOpenChange={(v) => !v && setHistoryOf(null)}>
        <DialogContent className="max-w-lg" data-testid="artifact-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><History className="h-5 w-5 text-primary" /> {historyOf?.original_filename}</DialogTitle>
          </DialogHeader>
          {!versions ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : (
            <div className="divide-y divide-border max-h-[50vh] overflow-y-auto">
              {versions.map((v) => (
                <div key={v.id} className="flex items-center gap-3 py-2.5" data-testid={`artifact-version-row-${v.id}`}>
                  <span className={`text-[11px] font-bold px-2 py-0.5 rounded shrink-0 ${v.is_current ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground"}`}>v{v.version}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-muted-foreground">{formatBytes(v.size)} · {v.uploaded_by_name || "Unknown"} · {new Date(v.created_at).toLocaleString()}</p>
                  </div>
                  <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => download(v)} data-testid={`artifact-version-download-${v.id}`}>
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FilterChip({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}
